import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import usePageMetadata from '../../hooks/usePageMetadata';
import materialPropertyGuides, {
  getMaterialPropertyGuide,
} from './data/materialPropertyGuides';
import materialDetailInsights, {
  machiningPriorityItems,
} from './data/materialDetailInsights';
import './MaterialPropertyGuide.css';

const guideInsightKeys = {
  'tensile-strength': 'tensileStrength',
  'thermal-conductivity': 'thermalConductivity',
  elongation: 'elongation',
  'chemical-composition': 'chemicalComposition',
};

const sharedMachinabilityNote =
  'Read each value as part of a wider material picture. Machinability is shaped by the combination of strength, hardness, ductility, thermal behavior, composition, structure, tool geometry and cutting conditions.';

const renderInsightSections = (sections) =>
  sections.map((section) => (
    <section key={section.title}>
      <h2>{section.title}</h2>
      {section.paragraphs &&
        section.paragraphs.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      {section.cards && (
        <div className="material-strength-direction-grid">
          {section.cards.map((card) => (
            <article className="material-strength-direction" key={card.title}>
              <h3>{card.title}</h3>
              <span>{card.text}</span>
            </article>
          ))}
        </div>
      )}
    </section>
  ));

const renderInsightRelationships = (relationships) => (
  <div className="material-strength-takeaway-grid">
    {relationships.map(({ label, result, Icon }) => (
      <div className="material-strength-takeaway" key={label}>
        <span>{label}</span>
        <Icon size={18} strokeWidth={1.9} aria-hidden="true" />
        <strong>{result}</strong>
      </div>
    ))}
  </div>
);

const renderMachiningPriorities = () => (
  <div className="material-machining-priority-grid">
    {machiningPriorityItems.map(({ label, Icon }) => (
      <div className="material-machining-priority" key={label}>
        <Icon size={18} strokeWidth={1.9} aria-hidden="true" />
        <span>{label}</span>
      </div>
    ))}
  </div>
);

const MaterialPropertyGuide = () => {
  const { propertySlug } = useParams();
  const guide = getMaterialPropertyGuide(propertySlug);
  const pageTitle = guide
    ? `${guide.title} and CNC Machining | ASiJoLab`
    : 'Material Property Guide | ASiJoLab';
  const pageDescription = guide
    ? `${guide.summary} See its practical relevance to CNC machining in ASiJoLab.`
    : 'Practical guides to material properties and their relevance to CNC machining.';

  usePageMetadata(pageTitle, pageDescription);

  if (!guide) {
    return <Navigate to="/materials" replace />;
  }

  const { Icon } = guide;
  const insightKey = guideInsightKeys[propertySlug];
  const insight = insightKey ? materialDetailInsights[insightKey] : null;

  if (!insight) {
    return <Navigate to="/materials" replace />;
  }

  return (
    <article className="material-property-detail">
      <Link className="material-property-back-link" to="/materials">
        <ArrowLeft size={17} strokeWidth={2} aria-hidden="true" />
        Back to Materials
      </Link>

      <header className="material-property-detail-header">
        <span className="material-property-detail-icon" aria-hidden="true">
          <Icon size={28} strokeWidth={1.9} />
        </span>
        <p className="material-property-eyebrow">Material property guide</p>
        <h1>{guide.title}</h1>
        <p>{insight.short}</p>
      </header>

      {insight.relationships && renderInsightRelationships(insight.relationships)}
      {insight.title === 'Tensile Strength' && renderMachiningPriorities()}
      {renderInsightSections(insight.sections)}

      <aside className="material-property-note">
        <h2>No single property decides machinability</h2>
        <p>{sharedMachinabilityNote}</p>
      </aside>

      <nav className="material-property-related" aria-label="Material property guides">
        {materialPropertyGuides.map((item) => (
          <Link
            key={item.slug}
            className={item.slug === guide.slug ? 'isActive' : undefined}
            to={`/materials/properties/${item.slug}`}
          >
            {item.title}
          </Link>
        ))}
      </nav>
    </article>
  );
};

export default MaterialPropertyGuide;
